import clsx from "clsx";
import React, { useLayoutEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { makeStyles } from "tss-react/mui";

import {
    Box,
    Paper,
    styled,
    Table,
    TableCell,
    TableCellProps,
    TableContainer,
    TableHead,
    TableSortLabel,
    TooltipProps,
    Typography,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";

import { CustomBadge } from "../CustomBadge";
import { Tooltip } from "../Tooltip";

import { useStyles as useTableHeaderStyles } from "./TableHeader.style";

export const tableRowHeight = 50;

export const MinimalTable = styled(Table)(({ theme }) => ({
    border: `1px solid ${theme.palette.emteria.border.secondary}`,
    borderCollapse: "collapse",
}));
export const MinimalTableHeader = styled(TableHead)(({ theme }) => ({
    position: "sticky",
    top: -0.5,
    zIndex: 2,
    "& .MuiTableRow-root": {
        border: `1px solid ${theme.palette.emteria.border.secondary}`,
    },
}));

type MinimalTableContainerProps = React.ComponentProps<typeof TableContainer> & {
    elevation?: number;
};

export const MinimalTableContainer = React.forwardRef<HTMLDivElement, MinimalTableContainerProps>(
    ({ elevation = 1, ...props }, ref) => <TableContainer ref={ref} component={Paper} elevation={elevation} {...props} />,
);

const useStyles = makeStyles()((theme: Theme) => ({
    tableCell: {
        height: tableRowHeight,
        fontSize: 16,
        padding: theme.spacing(1, 3),
        borderTop: "none",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        border: "none",
    },
}));

export const MinimalTableCell = ({ children, ...props }: TableCellProps) => {
    const { classes } = useStyles();
    return (
        <TableCell {...props} className={clsx(classes.tableCell, props.className)}>
            {children}
        </TableCell>
    );
};

interface CellWithTooltipProps {
    title: string;
    displayValue?: string;
    testid?: string;
    style?: React.CSSProperties;
    tooltipProps?: Omit<TooltipProps, "children" | "title">;
    highlight?: string;
}

export const MinimalTableCellWithTooltip = ({
    title,
    displayValue,
    testid,
    style,
    tooltipProps,
    highlight,
    ...rest
}: CellWithTooltipProps) => {
    const contentRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const theme = useTheme();
    const maxWidth = typeof style?.maxWidth === "number" ? `${style.maxWidth}px` : (style?.maxWidth ?? "200px");
    const text = displayValue ?? title;

    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el) {
            return;
        }
        setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [text, maxWidth]);

    return (
        <MinimalTableCell {...rest} style={{ ...style, overflow: "hidden", textOverflow: "clip", maxWidth }}>
            <Tooltip placement="top" title={title} hide={!isTruncated} {...tooltipProps}>
                <Typography
                    ref={contentRef}
                    component="span"
                    noWrap
                    data-testid={testid}
                    style={{ maxWidth, display: "inline-block" }}
                >
                    {highlight ? (
                        <Highlighter
                            searchWords={[highlight]}
                            textToHighlight={text}
                            autoEscape
                            unhighlightStyle={{ color: theme.palette.emteria.text.primary }}
                        />
                    ) : (
                        text
                    )}
                </Typography>
            </Tooltip>
        </MinimalTableCell>
    );
};

const StyledMinimalHeaderCell = styled((props: MinimalTableHeaderCellProps) => <TableCell {...props} />)(({ theme }) => ({
    height: tableRowHeight,
    padding: theme.spacing(0, 3, 0, 3),
    fontSize: 16,
    fontWeight: 600,
    backgroundColor: theme.palette.emteria.background.tableHeaders,
    color: `${theme.palette.emteria.text.primary} !important`,
    whiteSpace: "nowrap",
    "& .MuiTableSortLabel-root": {
        color: `${theme.palette.emteria.text.primary} !important`,
        fontWeight: 600,
        "&:hover": {
            color: `${theme.palette.emteria.text.primary} !important`,
        },
        "&.MuiTableSortLabel-active": {
            color: `${theme.palette.emteria.text.primary} !important`,
        },
        "& .MuiTableSortLabel-icon": {
            color: "inherit !important",
        },
    },
}));

type Item = {
    id: string;
    label: string;
};
interface MinimalTableHeaderCellProps extends TableCellProps {
    item?: Item;
    sort?: { orderBy: string; orderDirection: "asc" | "desc" };
    activeFiltersCount?: number;
    onSort?: (orderBy: string) => void;
    onFilter?: (item: Item) => void;
    sortableColumnIds?: string[];
    filterableColumnIds?: string[];
    align?: "inherit" | "left" | "center" | "right" | "justify";
    sortable?: boolean;
    sortDirection?: "asc" | "desc" | false;
    cellProps?: TableCellProps;
}
export const MinimalTableHeaderCell = (props: MinimalTableHeaderCellProps) => {
    const { classes } = useTableHeaderStyles();
    const { item, sort, activeFiltersCount, sortableColumnIds, filterableColumnIds, children, ...rest } = props;

    if (!item) {
        return <StyledMinimalHeaderCell {...rest}>{children}</StyledMinimalHeaderCell>;
    }

    const isSortable = sortableColumnIds?.includes(item.id);
    const isFilterable = filterableColumnIds?.includes(item.id);

    const onSort = () => {
        if (props.onSort) {
            props.onSort(item.id);
        }
    };

    const onFilter = () => {
        if (props.onFilter) {
            props.onFilter(item);
        }
    };

    if (isSortable) {
        return (
            <StyledMinimalHeaderCell align="left" sortDirection={sort?.orderDirection} {...props.cellProps}>
                <TableSortLabel
                    active={sort?.orderBy === item.id}
                    direction={sort?.orderDirection || "asc"}
                    className={classes.sortLabel}
                    onClick={onSort}
                >
                    <Typography>{item.label}</Typography>
                </TableSortLabel>
            </StyledMinimalHeaderCell>
        );
    }

    if (isFilterable) {
        return (
            <StyledMinimalHeaderCell {...props.cellProps}>
                <Box display="flex" alignItems="center" gap={3}>
                    <Typography component="span" fontSize={16} fontWeight={600} lineHeight={1.2}>
                        {item.label}
                    </Typography>

                    <CustomBadge
                        className={clsx(classes.badge, classes.withPointer)}
                        badgeContent={activeFiltersCount}
                        onClick={onFilter}
                        data-testid="tag-filter-button"
                        overlap="rectangular"
                    >
                        <i className="fa-solid fa-filter" />
                    </CustomBadge>
                </Box>
            </StyledMinimalHeaderCell>
        );
    }

    return (
        <StyledMinimalHeaderCell align="left" {...props.cellProps}>
            <Typography>{item.label}</Typography>
        </StyledMinimalHeaderCell>
    );
};
